import React, { useState, useEffect } from 'react';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { format, subDays, startOfDay } from 'date-fns';
import { getViewStats } from '../lib/supabase';
import { FiTrendingUp, FiUsers, FiEye, FiClock, FiGlobe } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const AdvancedAnalytics = ({ profileId }) => {
  const [analytics, setAnalytics] = useState({
    views: [],
    clicks: [],
    devices: [],
    locations: [],
    referrers: []
  });
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('7d');

  useEffect(() => {
    loadAnalytics();
  }, [profileId, timeRange]);

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      const { data: views } = await getViewStats(profileId);
      if (views) {
        const days = timeRange === '7d' ? 7 : timeRange === '30d' ? 30 : 90;
        const endDate = new Date();
        const startDate = subDays(endDate, days);

        // Process views data
        const processedViews = processViewsData(views, startDate, endDate);
        const deviceStats = processDeviceData(views);
        const locationStats = processLocationData(views);
        const referrerStats = processReferrerData(views);
        
        setAnalytics({
          views: processedViews,
          devices: deviceStats,
          locations: locationStats,
          referrers: referrerStats
        });
      }
    } catch (error) {
      console.error('Error loading analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const processViewsData = (views, startDate, endDate) => {
    const dailyViews = [];
    const days = timeRange === '7d' ? 7 : timeRange === '30d' ? 30 : 90;
    for (let i = 0; i < days; i++) {
      const date = startOfDay(subDays(endDate, i));
      const nextDate = startOfDay(subDays(endDate, i - 1));
      const dayViews = views.filter(view => {
        const viewDate = new Date(view.viewed_at);
        return viewDate >= date && viewDate < nextDate;
      });
      dailyViews.unshift({
        date: format(date, 'MMM dd'),
        views: dayViews.length,
        unique: new Set(dayViews.map(v => v.viewer_wallet)).size
      });
    }
    return dailyViews;
  };

  const processDeviceData = (views) => {
    // Mock device data - replace with actual user agent parsing
    return [
      { name: 'Desktop', value: 45, color: '#6366f1' },
      { name: 'Mobile', value: 35, color: '#8b5cf6' },
      { name: 'Tablet', value: 20, color: '#ec4899' }
    ];
  };
  
  const processLocationData = (views) => {
    // Mock location data - replace with actual IP geolocation
    return [
        { country: 'United States', views: 156, percentage: 35 },
        { country: 'United Kingdom', views: 89, percentage: 20 },
        { country: 'Germany', views: 67, percentage: 15 },
        { country: 'France', views: 45, percentage: 10 },
        { country: 'Japan', views: 34, percentage: 8 },
        { country: 'Other', views: 54, percentage: 12 }
    ];
  };

  const processReferrerData = (views) => {
    // Mock referrer data - replace with actual referrer tracking
    return [
      { source: 'Direct', value: 45, color: '#6366f1' },
      { source: 'Twitter', value: 25, color: '#1da1f2' },
      { source: 'Discord', value: 15, color: '#5865f2' },
      { source: 'Reddit', value: 10, color: '#ff4500' },
      { source: 'Other', value: 5, color: '#6b7280' }
    ];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-lime-500"></div>
      </div>
    );
  }

  const totalViews = analytics.views.reduce((sum, day) => sum + day.views, 0);
  const totalUnique = analytics.views.reduce((sum, day) => sum + day.unique, 0);
  const avgViewsPerDay = Math.round(totalViews / (analytics.views.length || 1));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-2xl font-bold text-white">Advanced Analytics</h3>
        <div className="flex items-center space-x-2">
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className="px-4 py-2 glass rounded-lg text-white border border-dark-700 focus:ring-2 focus:ring-lime-500 focus:border-lime-500"
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
          </select>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="glass-dark p-6 rounded-xl border border-dark-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-dark-400 mb-1">Total Views</p>
              <p className="text-2xl font-bold text-white">{totalViews.toLocaleString()}</p>
              <p className="text-xs text-lime-400 mt-1">+12% from last period</p>
            </div>
            <div className="w-12 h-12 bg-lime-500/20 rounded-lg flex items-center justify-center border border-lime-500/50">
              <SafeIcon icon={FiEye} className="text-lime-400 text-xl" />
            </div>
          </div>
        </div>
        <div className="glass-dark p-6 rounded-xl border border-dark-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-dark-400 mb-1">Unique Visitors</p>
              <p className="text-2xl font-bold text-white">{totalUnique.toLocaleString()}</p>
              <p className="text-xs text-lime-400 mt-1">+8% from last period</p>
            </div>
            <div className="w-12 h-12 bg-lime-500/20 rounded-lg flex items-center justify-center border border-lime-500/50">
              <SafeIcon icon={FiUsers} className="text-lime-400 text-xl" />
            </div>
          </div>
        </div>
        <div className="glass-dark p-6 rounded-xl border border-dark-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-dark-400 mb-1">Avg Daily Views</p>
              <p className="text-2xl font-bold text-white">{avgViewsPerDay}</p>
              <p className="text-xs text-red-400 mt-1">-3% from last period</p>
            </div>
            <div className="w-12 h-12 bg-lime-500/20 rounded-lg flex items-center justify-center border border-lime-500/50">
              <SafeIcon icon={FiTrendingUp} className="text-lime-400 text-xl" />
            </div>
          </div>
        </div>
        <div className="glass-dark p-6 rounded-xl border border-dark-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-dark-400 mb-1">Engagement Rate</p>
              <p className="text-2xl font-bold text-white">4.2%</p>
              <p className="text-xs text-lime-400 mt-1">+0.5% from last period</p>
            </div>
            <div className="w-12 h-12 bg-lime-500/20 rounded-lg flex items-center justify-center border border-lime-500/50">
              <SafeIcon icon={FiClock} className="text-lime-400 text-xl" />
            </div>
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Views Over Time */}
        <div className="glass-dark p-6 rounded-xl border border-dark-700">
          <h4 className="text-lg font-semibold text-white mb-4">Views Over Time</h4>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={analytics.views}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="views" stroke="#6366f1" strokeWidth={2} />
              <Line type="monotone" dataKey="unique" stroke="#8b5cf6" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Device Distribution */}
        <div className="glass-dark p-6 rounded-xl border border-dark-700">
          <h4 className="text-lg font-semibold text-white mb-4">Device Distribution</h4>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie data={analytics.devices} cx="50%" cy="50%" outerRadius={100} fill="#8884d8" dataKey="value" label>
                {analytics.devices.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Top Locations */}
        <div className="glass-dark p-6 rounded-xl border border-dark-700">
          <h4 className="text-lg font-semibold text-white mb-4">Top Locations</h4>
          <div className="space-y-3">
            {analytics.locations.map((location, index) => (
              <div key={location.country} className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-lime-500/20 rounded-lg flex items-center justify-center border border-lime-500/50">
                     <SafeIcon icon={FiGlobe} className="text-lime-400 text-sm" />
                  </div>
                  <span className="text-white font-medium">{location.country}</span>
                </div>
                <div className="flex items-center space-x-4">
                  <span className="text-dark-300">{location.views} views</span>
                  <div className="w-24 bg-dark-700 rounded-full h-2">
                    <div className="bg-lime-500 h-2 rounded-full" style={{ width: `${location.percentage}%` }}></div>
                  </div>
                  <span className="text-dark-300 text-sm w-12">{location.percentage}%</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Traffic Sources */}
        <div className="glass-dark p-6 rounded-xl border border-dark-700">
          <h4 className="text-lg font-semibold text-white mb-4">Traffic Sources</h4>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={analytics.referrers}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="source" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="value" fill="#6366f1">
                {analytics.referrers.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Export Options */}
      <div className="glass-dark p-6 rounded-xl border border-dark-700">
        <h4 className="text-lg font-semibold text-white mb-4">Export Analytics</h4>
        <div className="flex flex-wrap gap-4">
          <button className="glass-lime text-lime-400 px-4 py-2 rounded-lg font-medium hover:text-lime-300 transition-colors border border-lime-500/50">
            Export as CSV
          </button>
          <button className="glass text-white px-4 py-2 rounded-lg font-medium hover:text-lime-400 transition-colors border border-dark-700">
            Export as PDF
          </button>
          <button className="glass text-white px-4 py-2 rounded-lg font-medium hover:text-lime-400 transition-colors border border-dark-700">
            Schedule Report
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdvancedAnalytics;